const STORAGE_KEY = 'calcium_offline_mutation_queue_v1';
const MAX_QUEUE_SIZE = 100;
const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

let replayInProgress = false;

const isBrowser = () => typeof window !== 'undefined' && typeof localStorage !== 'undefined';

const normalizeMethod = (method) => (method || 'get').toLowerCase();

const isNetworkError = (error) => {
	if (typeof navigator !== 'undefined' && navigator.onLine === false) {
		return true;
	}
	return !error?.response && (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error');
};

const toAbsoluteUrl = (url, baseURL) => {
	if (!url) {
		return '';
	}
	try {
		return new URL(url, baseURL || window.location.origin).toString();
	} catch (_) {
		return url;
	}
};

const readQueue = () => {
	if (!isBrowser()) {
		return [];
	}
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) {
			return [];
		}
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed : [];
	} catch (_) {
		return [];
	}
};

const writeQueue = (queue) => {
	if (!isBrowser()) {
		return;
	}
	localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
};

const sanitizeHeaders = (headers) => {
	if (!headers) {
		return {};
	}
	if (typeof headers.toJSON === 'function') {
		return headers.toJSON();
	}
	const clean = {};
	Object.entries(headers).forEach(([key, value]) => {
		if (typeof value === 'string') {
			clean[key] = value;
		}
	});
	return clean;
};

const serializeData = (data) => {
	if (typeof data === 'undefined') {
		return { ok: true, data: undefined };
	}
	if (
		(typeof FormData !== 'undefined' && data instanceof FormData) ||
		(typeof Blob !== 'undefined' && data instanceof Blob) ||
		(typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer)
	) {
		return { ok: false, reason: 'Unsupported request payload for offline queue.' };
	}
	if (typeof data === 'string') {
		return { ok: true, data };
	}
	try {
		return { ok: true, data: JSON.parse(JSON.stringify(data)) };
	} catch (_) {
		return { ok: false, reason: 'Request payload could not be serialized.' };
	}
};

const shouldQueueRequest = (config, apiBaseUrl) => {
	if (!config || config.__fromOfflineQueue) {
		return false;
	}
	const method = normalizeMethod(config.method);
	if (!WRITE_METHODS.has(method)) {
		return false;
	}
	const absoluteUrl = toAbsoluteUrl(config.url, config.baseURL);
	if (!absoluteUrl) {
		return false;
	}
	if (apiBaseUrl) {
		try {
			const apiOrigin = new URL(apiBaseUrl).origin;
			if (!absoluteUrl.startsWith(apiOrigin)) {
				return false;
			}
		} catch (_) {
			if (!absoluteUrl.startsWith(apiBaseUrl)) {
				return false;
			}
		}
	}
	return true;
};

const enqueueRequest = (config) => {
	const serializedData = serializeData(config.data);
	if (!serializedData.ok) {
		return { queued: false, reason: serializedData.reason };
	}

	const queue = readQueue();
	queue.push({
		id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
		createdAt: Date.now(),
		url: config.url,
		baseURL: config.baseURL,
		method: normalizeMethod(config.method),
		data: serializedData.data,
		params: config.params || undefined,
		headers: sanitizeHeaders(config.headers),
		timeout: config.timeout,
	});

	if (queue.length > MAX_QUEUE_SIZE) {
		queue.splice(0, queue.length - MAX_QUEUE_SIZE);
	}

	writeQueue(queue);
	return { queued: true, size: queue.length };
};

const shouldDropReplayItem = (error) => {
	const status = error?.response?.status;
	if (!status) {
		return false;
	}
	if (status === 408 || status === 429) {
		return false;
	}
	return status >= 400 && status < 500;
};

const replayQueue = async (axiosInstance, { onQueueSizeChange, onSyncStateChange, onReplayComplete }) => {
	if (replayInProgress || !isBrowser()) {
		return;
	}
	const initialQueue = readQueue();
	if (initialQueue.length === 0) {
		onQueueSizeChange?.(0);
		return;
	}
	if (typeof navigator !== 'undefined' && navigator.onLine === false) {
		onQueueSizeChange?.(initialQueue.length);
		return;
	}

	replayInProgress = true;
	onSyncStateChange?.(true);

	let syncedCount = 0;
	let droppedCount = 0;
	const queue = [...initialQueue];

	try {
		while (queue.length > 0) {
			const next = queue[0];
			try {
				await axiosInstance({
					url: next.url,
					baseURL: next.baseURL,
					method: next.method,
					data: next.data,
					params: next.params,
					headers: {
						...next.headers,
						'X-Offline-Replay': '1',
					},
					timeout: next.timeout,
					__fromOfflineQueue: true,
				});
				queue.shift();
				syncedCount += 1;
				writeQueue(queue);
				onQueueSizeChange?.(queue.length);
			} catch (error) {
				if (isNetworkError(error)) {
					break;
				}
				if (shouldDropReplayItem(error)) {
					queue.shift();
					droppedCount += 1;
					writeQueue(queue);
					onQueueSizeChange?.(queue.length);
					continue;
				}
				break;
			}
		}
	} finally {
		replayInProgress = false;
		onSyncStateChange?.(false);
		onReplayComplete?.({
			syncedCount,
			droppedCount,
			remainingCount: queue.length,
		});
	}
};

export const initializeOfflineMutationQueue = (
	axiosInstance,
	{ apiBaseUrl, onQueueSizeChange, onSyncStateChange, onReplayComplete } = {}
) => {
	if (!axiosInstance) {
		return () => {};
	}

	const notifyQueueSize = () => onQueueSizeChange?.(readQueue().length);
	notifyQueueSize();

	const responseInterceptorId = axiosInstance.interceptors.response.use(
		(response) => response,
		(error) => {
			if (!isNetworkError(error) || !shouldQueueRequest(error?.config, apiBaseUrl)) {
				return Promise.reject(error);
			}

			const enqueueResult = enqueueRequest(error.config);
			if (enqueueResult.queued) {
				onQueueSizeChange?.(enqueueResult.size);
				const offlineError = new Error(
					'Action saved offline and will sync automatically when you are online.'
				);
				offlineError.name = 'OfflineQueueError';
				offlineError.isQueuedOffline = true;
				offlineError.queueSize = enqueueResult.size;
				return Promise.reject(offlineError);
			}

			return Promise.reject(error);
		}
	);

	const triggerReplay = () =>
		replayQueue(axiosInstance, { onQueueSizeChange, onSyncStateChange, onReplayComplete });

	const handleOnline = () => {
		triggerReplay();
	};

	window.addEventListener('online', handleOnline);

	if (typeof navigator !== 'undefined' && navigator.onLine) {
		void triggerReplay();
	}

	return () => {
		window.removeEventListener('online', handleOnline);
		axiosInstance.interceptors.response.eject(responseInterceptorId);
	};
};
